import { useApolloClient, useMutation } from '@base-app/web2'
import { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CREATE_DEPARTMENT, UPDATE_DEPARTMENT } from '../../../graphql/departments/mutations'
import { DEPARTMENT_QUERY, DEPARTMENTS_QUERY } from '../../../graphql/departments/queries'
import type { Department } from '../../../graphql/departments/types'
import { useRecordById } from '../../../hooks/useRecordById'
import { useToast } from '../../../hooks/useToast'
import { refetchQuietly } from '../../../lib/apolloRefetch'
import { dropSearchedLists, prependToLists } from '../../../lib/cacheLists'
import {
  focusFieldById,
  isRichTextTooLongError,
  isSortOrderInvalidError,
  isSubtitleTooLongError,
  MAX_RICH_TEXT_LENGTH,
} from '../../../lib/richText'
import { MAX_SORT_ORDER, parseSortOrder } from '../../../lib/sortOrder'
import { isSubtitleTooLong } from '../../../lib/subtitle'
import { lowResPathFor } from '../../../lib/uploads'
import { Button, FormActions } from '../../atoms'
import {
  FormField,
  FormLoadState,
  ImageUploadField,
  RichTextField,
  TextareaField,
} from '../../molecules'

interface DepartmentFormFieldsProps {
  department?: Department
  clientId: string
  appId: string
  onSuccess: () => void
  onClose: () => void
  // Renders every field as its pulsing placeholder while the record loads
  skeleton?: boolean
}

export const DepartmentFormFields = memo(
  ({ department, clientId, appId, onSuccess, onClose, skeleton }: DepartmentFormFieldsProps) => {
    const { t } = useTranslation('admin-mobifull')
    const { showToast } = useToast()
    const client = useApolloClient()
    const isEdit = !!department
    const [name, setName] = useState(department?.name ?? '')
    const [subtitle, setSubtitle] = useState(department?.subtitle ?? '')
    const [description, setDescription] = useState(department?.description ?? '')
    const [image, setImage] = useState(department?.image ?? '')
    const [uploadingImage, setUploadingImage] = useState(false)
    const [observations, setObservations] = useState(department?.observations ?? '')
    const [sortOrder, setSortOrder] = useState(
      department?.sort_order === null || department?.sort_order === undefined
        ? ''
        : String(department.sort_order),
    )
    const [errors, setErrors] = useState<{
      name?: string
      subtitle?: string
      description?: string
      sortOrder?: string
    }>({})

    const makeOptimistic = useCallback(
      (id: string): Department => ({
        __typename: 'Department',
        id,
        client_id: clientId,
        application_id: appId,
        name,
        subtitle: subtitle.trim() || null,
        description: description || null,
        image: image || null,
        image_low_res: lowResPathFor(image),
        observations: observations || null,
        sort_order: parseSortOrder(sortOrder) ?? null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }),
      [clientId, appId, name, subtitle, description, image, observations, sortOrder],
    )

    // The API's refusals belong to their fields; anything else is a toast
    const handleError = useCallback(
      (err: Error) => {
        if (isRichTextTooLongError(err)) {
          setErrors(current => ({
            ...current,
            description: t('departments.form.descriptionTooLong'),
          }))
          focusFieldById('dept-description')
        } else if (isSubtitleTooLongError(err)) {
          setErrors(current => ({ ...current, subtitle: t('departments.form.subtitleTooLong') }))
          focusFieldById('dept-subtitle')
        } else if (isSortOrderInvalidError(err)) {
          setErrors(current => ({ ...current, sortOrder: t('common.form.sortOrderInvalid') }))
          focusFieldById('dept-sort-order')
        } else {
          showToast(err.message, 'error')
        }
      },
      [showToast, t],
    )

    // The API orders by sort order, then name: the prepend is only the optimistic first guess, and an
    // update can move a row. Drop the searched lists and refetch the open ones once the server answered
    // (one extra list request per save, on purpose)
    const refreshLists = useCallback(() => {
      dropSearchedLists(client.cache, 'departments', { clientId, applicationId: appId })
      refetchQuietly(client.refetchQueries({ include: [DEPARTMENTS_QUERY] }))
    }, [client, clientId, appId])

    const [createDepartment, { loading: creating }] = useMutation(CREATE_DEPARTMENT, {
      optimisticResponse: { createDepartment: makeOptimistic(`temp-${Date.now()}`) },
      update(cache, result) {
        const item = result.data?.createDepartment
        if (!item) {
          return
        }
        prependToLists(cache, 'departments', item, { clientId, applicationId: appId })
      },
      onError: handleError,
      onCompleted() {
        refreshLists()
        onSuccess()
      },
    })

    const [updateDepartment, { loading: updating }] = useMutation(UPDATE_DEPARTMENT, {
      optimisticResponse: { updateDepartment: makeOptimistic(department?.id ?? '') },
      onError: handleError,
      onCompleted() {
        refreshLists()
        onSuccess()
      },
    })

    const loading = creating || updating || uploadingImage

    const validate = useCallback(() => {
      const errs: { name?: string; subtitle?: string; description?: string; sortOrder?: string } =
        {}
      if (!name.trim()) {
        errs.name = t('common.form.nameRequired')
      }
      if (isSubtitleTooLong(subtitle)) {
        errs.subtitle = t('departments.form.subtitleTooLong')
      }
      if (description.length > MAX_RICH_TEXT_LENGTH) {
        errs.description = t('departments.form.descriptionTooLong')
      }
      if (parseSortOrder(sortOrder) === undefined) {
        errs.sortOrder = t('common.form.sortOrderInvalid')
      }
      setErrors(errs)
      return Object.keys(errs).length === 0
    }, [name, subtitle, description, sortOrder, t])

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) {
          return
        }
        const vars = {
          name,
          subtitle: subtitle.trim() || null,
          description: description || null,
          image: image || null,
          observations: observations || null,
          sort_order: parseSortOrder(sortOrder) ?? null,
        }
        if (isEdit) {
          updateDepartment({ variables: { id: department.id, ...vars } })
        } else {
          createDepartment({ variables: { client_id: clientId, application_id: appId, ...vars } })
        }
      },
      [
        validate,
        isEdit,
        department?.id,
        name,
        subtitle,
        description,
        image,
        observations,
        sortOrder,
        clientId,
        appId,
        updateDepartment,
        createDepartment,
      ],
    )

    return (
      <form onSubmit={handleSubmit} noValidate aria-busy={skeleton}>
        {skeleton ? (
          <p className="sr-only" role="status">
            {t('common.loading')}
          </p>
        ) : null}
        <FormField
          skeleton={skeleton}
          id="dept-name"
          label={t('departments.form.nameLabel')}
          name="name"
          value={name}
          onChange={e => setName(e.target.value)}
          error={errors.name}
          required
        />
        <FormField
          skeleton={skeleton}
          id="dept-subtitle"
          label={t('departments.form.subtitleLabel')}
          name="subtitle"
          value={subtitle}
          onChange={e => setSubtitle(e.target.value)}
          error={errors.subtitle}
        />
        <ImageUploadField
          skeleton={skeleton}
          applicationId={appId}
          id="dept-image-file"
          target="departments"
          label={t('departments.form.imageLabel')}
          value={image}
          onChange={setImage}
          onUploadingChange={setUploadingImage}
        />
        <RichTextField
          skeleton={skeleton}
          id="dept-description"
          label={t('departments.form.descriptionLabel')}
          value={description}
          onChange={setDescription}
          error={errors.description}
        />
        <FormField
          skeleton={skeleton}
          id="dept-sort-order"
          label={t('common.form.sortOrderLabel')}
          name="sort_order"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={String(MAX_SORT_ORDER).length}
          value={sortOrder}
          onChange={e => setSortOrder(e.target.value)}
          error={errors.sortOrder}
          hint={t('common.form.sortOrderHint')}
        />
        <TextareaField
          skeleton={skeleton}
          id="dept-observations"
          label={t('departments.form.observationsLabel')}
          name="observations"
          value={observations}
          onChange={e => setObservations(e.target.value)}
        />
        <FormActions>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button skeleton={skeleton} type="submit" disabled={loading}>
            {loading ? t('departments.form.saving') : t('departments.form.save')}
          </Button>
        </FormActions>
      </form>
    )
  },
)

DepartmentFormFields.displayName = 'DepartmentFormFields'

interface DepartmentFormProps extends Omit<DepartmentFormFieldsProps, 'department'> {
  // The record to edit, by id; omit it to create a new one
  id?: string
}

// Loads the record by id (from the cache when the list has it, otherwise from the API)
export const DepartmentForm = memo(({ id, ...props }: DepartmentFormProps) => {
  const { record, status } = useRecordById(DEPARTMENT_QUERY, id, data => data.department)
  if (status === 'loading') {
    return <DepartmentFormFields skeleton {...props} />
  }
  if (status !== 'new' && status !== 'ready') {
    return <FormLoadState status={status} onClose={props.onClose} />
  }
  return <DepartmentFormFields key={id ?? 'new'} department={record} {...props} />
})

DepartmentForm.displayName = 'DepartmentForm'
